import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Button, Spin, message } from 'antd';
import {
    CheckOutlined,
    CrownFilled,
    RocketFilled,
    ThunderboltFilled,
    StarFilled,
    FireFilled,
    DashboardFilled,
    CloseOutlined,
    BankOutlined,
    CopyOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import AuthService from '../../services/authService';
import TossPaymentModal from '../../components/Payment/TossPaymentModal';
import WireTransferModal from '../../components/Payment/WireTransferModal';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';

const { Title, Text } = Typography;

// 여러 테마 색상 프리셋 (관리자가 추가로 생성할 경우를 대비)
const THEME_PRESETS = [
    { color: '#595959', bg: '#ffffff', icon: <ThunderboltFilled /> },
    { color: '#2f54eb', bg: '#f0f5ff', icon: <RocketFilled /> },
    { color: '#d48806', bg: '#fffbe6', icon: <CrownFilled /> },
    { color: '#722ed1', bg: '#f9f0ff', icon: <DashboardFilled /> },
    { color: '#eb2f96', bg: '#fff0f6', icon: <FireFilled /> },
    { color: '#13c2c2', bg: '#e6fffb', icon: <StarFilled /> },
];

export const GradeGuide = () => {
    const [grades, setGrades] = useState([]);
    const [queryLimits, setQueryLimits] = useState([]);
    const [rowLimits, setRowLimits] = useState([]);
    const [_features, setFeatures] = useState([]);
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bankName, setBankName] = useState('국민은행');
    const [bankAccount, setBankAccount] = useState('123-456-789012');
    const [bankHolder, setBankHolder] = useState('대표자명');
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [isWireTransferModalVisible, setIsWireTransferModalVisible] = useState(false);
    const [selectedGrade, setSelectedGrade] = useState(null);

    const currentUser = AuthService.getUserInfo();
    const currentGrade = currentUser?.grade || 'user';

    const handlePaymentClick = grade => {
        if (currentGrade !== 'user') {
            message.warning(
                `이미 유료 요금제(${currentGrade === 'basic' ? '베이직' : '프리미엄'})를 이용 중입니다. 요금제 변경 및 연장은 고객센터로 문의해 주세요.`
            );
            return;
        }
        setSelectedGrade(grade);
        setIsPaymentModalVisible(true);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await AuthService.authAxios.get('/settings/grades-info');
                if (res.data?.success) {
                    setGrades(
                        res.data.data.filter(
                            g => g.grade_name !== 'admin' && g.grade_name !== 'manager'
                        )
                    );
                    setQueryLimits(res.data.queryLimits || []);
                    setRowLimits(res.data.rowLimits || []);
                    setMenus(res.data.menus || []);

                    try {
                        const featRes = await AuthService.authAxios.get('/settings/features');
                        if (featRes.data?.success) {
                            setFeatures(featRes.data.data || []);
                        }
                    } catch (e) {
                        console.error('Failed to fetch features', e);
                    }

                    if (res.data.systemSettings) {
                        if (res.data.systemSettings.bank_name)
                            setBankName(res.data.systemSettings.bank_name);
                        if (res.data.systemSettings.bank_account)
                            setBankAccount(res.data.systemSettings.bank_account);
                        if (res.data.systemSettings.bank_holder)
                            setBankHolder(res.data.systemSettings.bank_holder);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch guide info:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const getGradeTheme = (gradeName, index) => {
        if (gradeName === 'user') return THEME_PRESETS[0];
        if (gradeName === 'basic') return THEME_PRESETS[5];
        if (gradeName === 'premium') return THEME_PRESETS[1];
        if (gradeName === 'vip') return THEME_PRESETS[2];
        return THEME_PRESETS[index % THEME_PRESETS.length];
    };

    const currentIndex = grades.findIndex(g => g.grade_name === currentGrade);

    const _tableColumns = [
        {
            title: (
                <span style={{ fontSize: '15px', color: '#8c8c8c', fontWeight: 500 }}>
                    기능 및 도구
                </span>
            ),
            dataIndex: 'menuLabel',
            key: 'menuLabel',
            width: '24%',
            render: text => <strong style={{ fontSize: '15px', color: '#262626' }}>{text}</strong>,
        },
        ...grades.map((g, index) => {
            const theme = getGradeTheme(g.grade_name, index);
            const isPremium = g.grade_name === 'premium';

            return {
                title: (
                    <div
                        style={{
                            textAlign: 'center',
                            color: theme.color,
                            fontSize: '18px',
                            fontWeight: 800,
                            padding: '16px 0',
                        }}
                    >
                        {g.grade_name.toUpperCase()}
                    </div>
                ),
                dataIndex: g.grade_name,
                key: g.grade_name,
                align: 'center',
                width: `${76 / (grades.length || 1)}%`,
                className: isPremium ? 'premium-column' : '',
                render: (val, record) => {
                    if (!val) return null;

                    // 특별 행(행수 한도) 처리
                    if (record.isSpecialRow) {
                        const str = val.rowLimit === 0 ? '무제한' : `${val.rowLimit}개`;
                        const colr = val.rowLimit === 0 ? '#1890ff' : '#262626';
                        return <strong style={{ color: colr, fontSize: '18px' }}>{str}</strong>;
                    }

                    if (val.daily === -1) {
                        return (
                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                지원하지 않음
                            </Text>
                        );
                    }

                    const isDailyUnlimited = val.daily === 0;
                    const dailyStr = isDailyUnlimited ? '무제한' : `${val.daily}회`;
                    const dailyColor = isDailyUnlimited ? '#1890ff' : '#262626';

                    let rowStr = '-';
                    let rowColor = '#bfbfbf';

                    if (val.hasRowLimit) {
                        const isRowUnlimited = val.rowLimit === 0;
                        rowStr =
                            val.rowLimit === null || val.rowLimit === undefined
                                ? '-'
                                : isRowUnlimited
                                  ? '무제한'
                                  : `${val.rowLimit}개`;
                        rowColor = isRowUnlimited ? '#1890ff' : '#262626';
                    }

                    // 색상을 빼고 여백과 타이포그래피만으로 깔끔하게 정렬
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '120px',
                                }}
                            >
                                <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                    일일 사용량
                                </span>
                                <strong style={{ color: dailyColor, fontSize: '14px' }}>
                                    {dailyStr}
                                </strong>
                            </div>
                            {val.hasRowLimit && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '120px',
                                    }}
                                >
                                    <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                        1회 최대행수
                                    </span>
                                    <strong style={{ color: rowColor, fontSize: '14px' }}>
                                        {rowStr}
                                    </strong>
                                </div>
                            )}
                        </div>
                    );
                },
            };
        }),
    ];

    const rowLimitData = {
        key: 'max_rows',
        menuLabel: '🚀 1회 대량 조회 행수 한도',
        isSpecialRow: true,
    };
    grades.forEach(g => {
        const rLimit = rowLimits.find(
            rl =>
                rl.grade_name === g.grade_name &&
                String(rl.gubun).toLowerCase() === 'integratedSearch'.toLowerCase()
        );
        const mRows = rLimit ? rLimit.max_rows : 20;

        rowLimitData[g.grade_name] = {
            daily: null,
            rowLimit: mRows,
            hasRowLimit: true,
        };
    });

    const featureRows = menus.map(m => {
        const row = { key: m.menu_key || m.path, menuLabel: m.label };
        const rawGubun = m.rank_row_gubun;
        const rowGubunStr = rawGubun ? String(rawGubun).split(',')[0].trim() : null;

        grades.forEach(g => {
            const qLimit = queryLimits.find(
                ql =>
                    ql.grade_name === g.grade_name &&
                    String(ql.query_type).toLowerCase() === String(m.menu_key).toLowerCase()
            );
            const daily = qLimit ? qLimit.daily_limit : 0;

            let rowLimit = null;
            let hasRowLimit = false;
            if (rowGubunStr) {
                hasRowLimit = true;
                const rLimit = rowLimits.find(
                    rl =>
                        rl.grade_name === g.grade_name &&
                        String(rl.gubun).toLowerCase() === rowGubunStr.toLowerCase()
                );
                rowLimit = rLimit ? rLimit.max_rows : 20;
            }

            row[g.grade_name] = { daily, rowLimit, hasRowLimit };
        });

        return row;
    });

    const _tableData = [rowLimitData, ...featureRows];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <ProtectedMenuPage menuKey="gradeGuide">
            <div style={{ background: '#fafafa', minHeight: 'calc(100vh - 80px)', paddingBottom: '40px' }}>
                <style>{`
                .premium-column {
                    background-color: #fafcff !important;
                }
                .ant-table-wrapper .ant-table {
                    background: transparent !important;
                }
                .ant-table-thead > tr > th {
                    background: transparent !important;
                    border-bottom: 2px solid #e8e8e8 !important;
                }
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f0f0 !important;
                    padding: 20px 16px !important;
                    transition: all 0.3s;
                }
                .ant-table-tbody > tr:hover > td {
                    background-color: #fff !important;
                    box-shadow: 0 -1px 0 0 #f0f0f0 inset;
                }
                .ant-table-tbody > tr:hover > td.premium-column {
                    background-color: #f0f5ff !important;
                }
            `}</style>

                <div
                    style={{
                        background: '#0a0a0a',
                        padding: '80px 24px 140px',
                        textAlign: 'center',
                    }}
                >
                    <Title
                        level={1}
                        style={{
                            color: '#fff',
                            fontSize: '42px',
                            fontWeight: 800,
                            marginBottom: '16px',
                            letterSpacing: '-1px',
                        }}
                    >
                        비즈니스의 성장을 위한 최적의 플랜
                    </Title>
                    <Text style={{ color: '#a6a6a6', fontSize: '18px', fontWeight: 400 }}>
                        압도적인 대량 조회 인프라. 지금 바로 비즈니스 스케일에 맞는 등급을
                        선택하세요.
                    </Text>
                </div>

                <div
                    style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 24px',
                        marginTop: '-80px',
                    }}
                >
                    <Row gutter={[24, 24]} justify="center">
                        {grades.map((grade, index) => {
                            const theme = getGradeTheme(grade.grade_name, index);
                            const isCurrent = currentGrade === grade.grade_name;
                            const isMostPopular = grade.grade_name === 'premium';
                            const isPaid = grade.grade_name !== 'user';

                            const isDowngrade = currentIndex !== -1 && index < currentIndex;
                            let buttonText = '결제 / 구독하기';
                            let buttonVisibility = 'visible';
                            let buttonDisabled = false;

                            if (isCurrent) {
                                buttonText = '현재 이용중';
                                // 스타일을 커스텀하기 위해 disabled 속성 대신 onClick 로직으로 차단
                            } else if (isDowngrade) {
                                buttonText = '';
                                buttonVisibility = 'hidden';
                            }

                            const rLimit = rowLimits.find(
                                rl =>
                                    rl.grade_name === grade.grade_name &&
                                    String(rl.gubun).toLowerCase() ===
                                        'integratedSearch'.toLowerCase()
                            );
                            const _maxRows = rLimit ? rLimit.max_rows : 20;

                            return (
                                <Col
                                    xs={24}
                                    md={Math.max(6, Math.floor(24 / grades.length))}
                                    xl={Math.max(4, Math.floor(24 / grades.length))}
                                    key={grade.id}
                                    style={{ display: 'flex' }}
                                >
                                    <div
                                        style={{
                                            background: '#fff',
                                            borderRadius: '16px',
                                            padding: '40px 32px',
                                            boxShadow: isMostPopular
                                                ? `0 20px 40px ${theme.color}22`
                                                : '0 8px 24px rgba(0,0,0,0.04)',
                                            border: isPaid
                                                ? `2px solid ${theme.color}`
                                                : '1px solid #f0f0f0',
                                            width: '100%',
                                            position: 'relative',
                                            transition: 'transform 0.3s',
                                            transform: isMostPopular ? 'translateY(-10px)' : 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {isMostPopular && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '-18px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    background: `linear-gradient(135deg, ${theme.color}, #722ed1)`,
                                                    color: '#fff',
                                                    borderRadius: '24px',
                                                    padding: '6px 20px',
                                                    fontSize: '13px',
                                                    fontWeight: 800,
                                                    boxShadow: `0 6px 16px ${theme.color}55`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 2,
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                <CrownFilled
                                                    style={{ fontSize: '15px', color: '#ffec3d' }}
                                                />
                                                MOST POPULAR
                                            </div>
                                        )}

                                        <div style={{ marginBottom: '24px' }}>
                                            <div
                                                style={{
                                                    fontSize: '28px',
                                                    color: theme.color,
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                {theme.icon}
                                            </div>
                                            <Title
                                                level={3}
                                                style={{
                                                    margin: 0,
                                                    fontSize: '24px',
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {grade.grade_name.toUpperCase()}
                                            </Title>
                                            <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                                {grade.description || '기본 제공'}
                                            </Text>
                                        </div>

                                        <div style={{ marginBottom: '32px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '4px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: '32px',
                                                        fontWeight: 800,
                                                        color: theme.color,
                                                        letterSpacing: '-1px',
                                                    }}
                                                >
                                                    {index === 0
                                                        ? 'Free'
                                                        : `${(grade.price || 50000).toLocaleString()}원`}
                                                </div>
                                                {index !== 0 && (
                                                    <span
                                                        style={{
                                                            fontSize: '16px',
                                                            color: '#8c8c8c',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        / 월
                                                        <span style={{ fontSize: '11px', fontWeight: 400, marginLeft: '4px' }}>
                                                            (VAT 10% 별도)
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    fontSize: '13px',
                                                    display: 'block',
                                                    marginTop: '4px',
                                                }}
                                            >
                                                {index === 0
                                                    ? '영구 무료 이용'
                                                    : '1개월(30일) 단위 이용권 (정기·단건 결제)'}
                                            </Text>
                                        </div>

                                        <div style={{ flex: 1, marginBottom: '32px' }}>
                                            <ul
                                                style={{ listStyle: 'none', padding: 0, margin: 0 }}
                                            >
                                                {/* 메뉴별 상세 제공량 */}
                                                {menus.map(m => {
                                                    const qLimit = queryLimits.find(
                                                        ql =>
                                                            ql.grade_name === grade.grade_name &&
                                                            String(ql.query_type).toLowerCase() ===
                                                                String(
                                                                    m.menu_key || m.path
                                                                ).toLowerCase()
                                                    );
                                                    const daily = qLimit ? qLimit.daily_limit : 0;

                                                    const rawGubun = m.rank_row_gubun;
                                                    const rowGubunStr = rawGubun
                                                        ? String(rawGubun).split(',')[0].trim()
                                                        : null;
                                                    let rowLimit = null;
                                                    let hasRowLimit = false;
                                                    if (rowGubunStr) {
                                                        hasRowLimit = true;
                                                        const rl = rowLimits.find(
                                                            r =>
                                                                r.grade_name === grade.grade_name &&
                                                                String(r.gubun).toLowerCase() ===
                                                                    rowGubunStr.toLowerCase()
                                                        );
                                                        rowLimit = rl ? rl.max_rows : 20;
                                                    }

                                                    const isDailyUnlimited = daily === 0;
                                                    const dailyStr = isDailyUnlimited
                                                        ? '무제한'
                                                        : `${daily}회`;

                                                    let rowStr = '-';
                                                    let isRowUnlimited = false;
                                                    if (hasRowLimit) {
                                                        isRowUnlimited = rowLimit === 0;
                                                        rowStr =
                                                            rowLimit === null ||
                                                            rowLimit === undefined
                                                                ? '-'
                                                                : isRowUnlimited
                                                                  ? '무제한'
                                                                  : `${rowLimit}개`;
                                                    }

                                                    // 지원 안하는 기능도 동일한 높이의 폼을 유지하여 좌우 대칭이 무너지지 않도록 함
                                                    return (
                                                        <li
                                                            key={m.menu_key || m.path}
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                marginBottom: '12px',
                                                                border: '1px solid #f0f0f0',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                opacity: daily === -1 ? 0.5 : 1,
                                                            }}
                                                        >
                                                            {/* 타이틀(헤더) 영역 */}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '10px 14px',
                                                                    background:
                                                                        isPaid && daily !== -1
                                                                            ? theme.bg
                                                                            : '#fafafa',
                                                                    borderBottom:
                                                                        '1px solid #f0f0f0',
                                                                }}
                                                            >
                                                                {daily === -1 ? (
                                                                    <CloseOutlined
                                                                        style={{
                                                                            color: '#ff4d4f',
                                                                            marginRight: '8px',
                                                                            fontSize: '14px',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <CheckOutlined
                                                                        style={{
                                                                            color: theme.color,
                                                                            marginRight: '8px',
                                                                            fontSize: '14px',
                                                                        }}
                                                                    />
                                                                )}
                                                                <Text
                                                                    style={{
                                                                        fontSize: '14px',
                                                                        fontWeight: 800,
                                                                        color:
                                                                            daily === -1
                                                                                ? '#8c8c8c'
                                                                                : '#262626',
                                                                        textDecoration:
                                                                            daily === -1
                                                                                ? 'line-through'
                                                                                : 'none',
                                                                    }}
                                                                >
                                                                    {m.label}
                                                                </Text>
                                                            </div>

                                                            {/* 데이터(바디) 영역 */}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    padding: '10px 14px',
                                                                    background: '#fff',
                                                                    gap: '8px',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: '12px',
                                                                            color: '#8c8c8c',
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        일일 조회 한도
                                                                    </span>
                                                                    <span
                                                                        style={{
                                                                            fontSize: '14px',
                                                                            fontWeight: 800,
                                                                            color:
                                                                                daily === -1
                                                                                    ? '#bfbfbf'
                                                                                    : isDailyUnlimited
                                                                                      ? theme.color
                                                                                      : '#262626',
                                                                        }}
                                                                    >
                                                                        {daily === -1
                                                                            ? '지원 안함'
                                                                            : dailyStr}
                                                                    </span>
                                                                </div>
                                                                {hasRowLimit && (
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            justifyContent:
                                                                                'space-between',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                fontSize: '12px',
                                                                                color: '#8c8c8c',
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            1회 최대행수
                                                                        </span>
                                                                        <span
                                                                            style={{
                                                                                fontSize: '14px',
                                                                                fontWeight: 800,
                                                                                color:
                                                                                    daily === -1
                                                                                        ? '#bfbfbf'
                                                                                        : isRowUnlimited
                                                                                          ? theme.color
                                                                                          : '#262626',
                                                                            }}
                                                                        >
                                                                            {daily === -1
                                                                                ? '-'
                                                                                : rowStr}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>

                                        <Button
                                            type={isPaid && !isCurrent ? 'primary' : 'default'}
                                            size="large"
                                            block
                                            disabled={buttonDisabled}
                                            onClick={() => !isCurrent && handlePaymentClick(grade)}
                                            style={{
                                                minHeight: '48px',
                                                height: '48px',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                fontWeight: 700,
                                                backgroundColor: isCurrent
                                                    ? '#e6f4ff'
                                                    : isPaid
                                                      ? theme.color
                                                      : '#fff',
                                                borderColor: isCurrent
                                                    ? '#1677ff'
                                                    : isPaid
                                                      ? theme.color
                                                      : '#d9d9d9',
                                                color: isCurrent
                                                    ? '#1677ff'
                                                    : isPaid
                                                      ? '#fff'
                                                      : '#262626',
                                                marginTop: 'auto',
                                                visibility: buttonVisibility,
                                                cursor: isCurrent ? 'default' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <span style={{ fontSize: '20px', fontWeight: 800 }}>
                                                {buttonText}
                                            </span>
                                        </Button>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>

                    <div
                        style={{
                            marginTop: '32px',
                            borderRadius: '16px',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f4f8ff 100%)',
                            border: '1px solid #d6e4ff',
                            padding: '24px 32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '24px',
                            flexWrap: 'wrap',
                            boxShadow: '0 8px 24px rgba(22, 119, 255, 0.08)',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                                flex: '1 1 auto',
                            }}
                        >
                            <div
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '14px',
                                    background: 'linear-gradient(135deg, #1677ff 0%, #69b1ff 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)',
                                }}
                            >
                                <BankOutlined style={{ fontSize: '24px', color: '#fff' }} />
                            </div>
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                    }}
                                >
                                    <Title
                                        level={5}
                                        style={{
                                            margin: 0,
                                            fontWeight: 800,
                                            color: '#141414',
                                            letterSpacing: '-0.5px',
                                        }}
                                    >
                                        무통장 입금 안내
                                    </Title>
                                </div>
                                <div
                                    style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}
                                >
                                    <Text
                                        style={{
                                            fontSize: '16px',
                                            color: '#595959',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {bankName}
                                    </Text>
                                    <strong
                                        style={{
                                            fontSize: '24px',
                                            color: '#1677ff',
                                            fontWeight: 900,
                                            fontFamily: 'Consolas, monospace',
                                            lineHeight: '1',
                                        }}
                                    >
                                        {bankAccount}
                                    </strong>
                                    <Text style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        (예금주: {bankHolder})
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                flex: '0 0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}
                        >
                            <Button
                                size="large"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(bankAccount);
                                    message.success('계좌번호가 복사되었습니다.');
                                }}
                                style={{
                                    borderRadius: '8px',
                                    color: '#1677ff',
                                    borderColor: '#91caff',
                                    fontWeight: 600,
                                }}
                            >
                                계좌복사
                            </Button>
                            <Button
                                type={currentUser?.grade !== 'user' ? 'default' : 'primary'}
                                size="large"
                                icon={<FileTextOutlined />}
                                onClick={() => {
                                    if (currentUser?.grade !== 'user') {
                                        message.warning(
                                            `이미 유료 요금제(${currentGrade === 'basic' ? '베이직' : '프리미엄'})를 이용 중입니다. 요금제 변경 및 연장은 고객센터로 문의해 주세요.`
                                        );
                                        return;
                                    }
                                    const defaultGrade = grades.find(
                                        g => g.grade_name === 'basic'
                                    ) || { grade_name: 'basic', price: 30000 };
                                    setSelectedGrade(defaultGrade);
                                    setIsWireTransferModalVisible(true);
                                }}
                                disabled={currentUser?.grade !== 'user'}
                                style={
                                    currentUser?.grade !== 'user'
                                        ? {
                                              borderRadius: '8px',
                                              fontWeight: 'bold',
                                              boxShadow: 'none',
                                              backgroundColor: '#f5f5f5',
                                              borderColor: '#d9d9d9',
                                              color: '#bfbfbf',
                                          }
                                        : {
                                              borderRadius: '8px',
                                              fontWeight: 'bold',
                                              boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)',
                                          }
                                }
                            >
                                {currentUser?.grade !== 'user'
                                    ? '유료 요금제 이용 중'
                                    : '입금확인요청'}
                            </Button>
                        </div>
                    </div>
                </div>

                <KakaoFloatButton />

                <WireTransferModal
                    visible={isWireTransferModalVisible}
                    onClose={() => setIsWireTransferModalVisible(false)}
                    grade={selectedGrade}
                    amount={
                        selectedGrade?.price ||
                        (selectedGrade?.grade_name === 'basic' ? 30000 : 50000)
                    }
                />

                <TossPaymentModal
                    visible={isPaymentModalVisible}
                    onClose={() => setIsPaymentModalVisible(false)}
                    grade={selectedGrade}
                    amount={
                        selectedGrade?.price ||
                        (selectedGrade?.grade_name === 'basic' ? 30000 : 50000)
                    }
                    user={currentUser}
                />
            </div>
        </ProtectedMenuPage>
    );
};
